import React, { Dispatch, SetStateAction } from 'react';
import { Text, StyleSheet, Platform } from 'react-native';
import { useSelector } from 'react-redux';
import Style from '../../../../../constants/Style';
import {
  Template, TemplateGroup, TemplateType, TemplateTypeTitles, UserPermission,
} from '../../../../../constants/Types';
import DropdownIcon12 from '../../../../12pxIcons/DropdownIcon12';
import Colors from '../../../../../constants/Colors';
import TemplateGroupList from './components/TemplateGroupList';
import PressableStyled from '../../../../PressableStyled';
import TemplateTypeList from './components/TemplateTypeList';
import CustomTextField from '../../../../CustomTextField';

interface MainSectionProps {
  templateGroupNotSelected: boolean,
  templateTypeDropdownShown: boolean,
  setTemplateTypeDropdownShown: Dispatch<SetStateAction<boolean>>,
  templateGroupDropdownShown: boolean,
  setTemplateGroupDropdownShown: Dispatch<SetStateAction<boolean>>,
  setFieldTypesShownIndex: Dispatch<SetStateAction<number | null>>,
  templateGroups: TemplateGroup[],
  templates: Template[],
  selectedTemplateGroupId: string,
  setSelectedTemplateGroupId: Dispatch<SetStateAction<string>>,
  selectedTemplateName: string,
  setSelectedTemplateName: Dispatch<SetStateAction<string>>,
  nameField,
  closeDropdowns: () => void,
  selectedTemplateType: TemplateType,
  setSelectedTemplateType: Dispatch<SetStateAction<TemplateType>>,
  happyEnabled: boolean,
  templateTitle: string | null,
  setTemplateTitle: Dispatch<SetStateAction<string | null>>,
  templateSubtitle: string | null,
  setTemplateSubtitle: Dispatch<SetStateAction<string | null>>,
  templateSubtitleUrl: string | null,
  setTemplateSubtitleUrl: Dispatch<SetStateAction<string | null>>,
  currentTemplate: Template | null,
  subtitleUrlInvalid: boolean,
  templateNameInvalid: boolean,
  templateErrorMessage: () => string | null,
}

const MainSection = ({
  templateGroupNotSelected, templateGroupDropdownShown, setTemplateGroupDropdownShown, nameField,
  setFieldTypesShownIndex, templateGroups, selectedTemplateGroupId, setSelectedTemplateGroupId,
  selectedTemplateName, setSelectedTemplateName, closeDropdowns, templateTypeDropdownShown,
  setTemplateTypeDropdownShown, selectedTemplateType, setSelectedTemplateType, happyEnabled,
  templateTitle, setTemplateTitle, templateSubtitle, setTemplateSubtitle, templateSubtitleUrl,
  setTemplateSubtitleUrl, currentTemplate, subtitleUrlInvalid, templateNameInvalid,
  templateErrorMessage,
}: MainSectionProps) => {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canEditTemplates: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_UPDATE);
  const typeDropdownDisabled: boolean = !!currentTemplate || !canEditTemplates;

  return <>
    <Text style={styles.initialOptionTitle}>
      {'Template\'s settings'}
    </Text>
    <Text style={styles.nameLabel}>
      {happyEnabled ? 'Give template name, choose it\'s type and icon' : 'Give template a name'}
    </Text>
    {happyEnabled ? <>
      <Text style={[styles.nameLabel, styles.textSpacing]}>
        {'Type'}
      </Text>
      <PressableStyled
        style={[styles.templateGroupIndicator, Style.row]}
        onPress={() => {
          if (currentTemplate) return;
          setFieldTypesShownIndex(null);
          setTemplateTypeDropdownShown(!templateTypeDropdownShown);
        }}
        testID='select-template-type-btn'
        disabled={typeDropdownDisabled}
      >
        <Text style={styles.templateGroupText}>
          {TemplateTypeTitles[selectedTemplateType]}
        </Text>
        {!typeDropdownDisabled && <DropdownIcon12
          color={Colors.light.hereworksBlue500}
          style={templateTypeDropdownShown ? Style.inverted : Style.upright}
        />}
      </PressableStyled>
      {currentTemplate && <Text style={styles.infoMessage}>
        {'Template type cannot be edited as there are assets belonging to this template'}
        {', located in some/all building(s)'}
      </Text>}
      <TemplateTypeList
        {...{
          templateTypeDropdownShown,
          setTemplateTypeDropdownShown,
          selectedTemplateType,
          setSelectedTemplateType,
        }}
      />
    </> : null}
    <CustomTextField
      testID='template-name-input-field'
      ref={nameField}
      title='Name *'
      value={selectedTemplateName}
      updateValue={setSelectedTemplateName}
      onFocus={closeDropdowns}
      errorText={templateErrorMessage()}
      invalid={templateNameInvalid}
      editable={canEditTemplates}
    />
    <CustomTextField
      title='Title'
      testID='template-title-input-field'
      onFocus={closeDropdowns}
      value={templateTitle}
      updateValue={setTemplateTitle}
      editable={canEditTemplates}
    />
    <CustomTextField
      title='Subtitle'
      testID='template-subtitle-input-field'
      onFocus={closeDropdowns}
      value={templateSubtitle}
      updateValue={setTemplateSubtitle}
      editable={canEditTemplates}
    />
    <CustomTextField
      title='Subtitle URL'
      testID='template-subtitle-url-input-field'
      onFocus={closeDropdowns}
      value={templateSubtitleUrl}
      updateValue={setTemplateSubtitleUrl}
      editable={canEditTemplates}
      errorText='Text entered is not a valid URL'
      invalid={subtitleUrlInvalid}
      placeholder='Enter a valid URL for subtitle URL (e.g. https://example.com)'
    />
    <Text style={[styles.nameLabel, styles.textSpacing]}>{'Template group *'}</Text>
    <PressableStyled
      style={[
        styles.templateGroupIndicator, Style.row,
        templateGroupNotSelected && { borderColor: styles.errorText.color },
      ]}
      onPress={() => {
        setFieldTypesShownIndex(null);
        setTemplateGroupDropdownShown(!templateGroupDropdownShown);
      }}
      testID='select-tmplt-group-btn'
      disabled={!canEditTemplates}
    >
      <Text style={styles.templateGroupText}>
        {templateGroups?.filter((group: TemplateGroup) => group.ButtonPrototypeGroupId
          === selectedTemplateGroupId)[0]?.Name ?? 'Select a template group'}
      </Text>
      {canEditTemplates && <DropdownIcon12
        color={Colors.light.hereworksBlue500}
        style={templateGroupDropdownShown ? Style.inverted : Style.upright}
      />}
    </PressableStyled>
    <TemplateGroupList
      {...{
        templateGroupDropdownShown,
        setTemplateGroupDropdownShown,
        templateGroups,
        selectedTemplateGroupId,
        setSelectedTemplateGroupId,
      }}
    />
    {templateGroupNotSelected && <Text style={styles.errorText}>
      {'Please select a template group'}
    </Text>}
  </>;
};

const styles = StyleSheet.create({
  initialOptionTitle: {
    fontSize: 20,
    lineHeight: 30,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
  },
  nameLabel: {
    fontSize: 16,
    lineHeight: Platform.OS === 'ios' ? undefined : 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginBottom: 4,
  },
  errorText: {
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksRed500,
  },
  textSpacing: {
    marginTop: 12,
  },
  templateGroupIndicator: {
    alignSelf: 'stretch',
    paddingHorizontal: 12,
    paddingVertical: 9,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
  },
  templateGroupText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    flex: 1,
  },
  infoMessage: {
    marginTop: 4,
    fontSize: 12,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
});

export default MainSection;
